import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";

import { attendanceService, type AttendanceRecord } from "../../services/attendanceService";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { PERIODS, STATUSES, clock, day, recordConfig, registerLabel, statusDot, statusOf } from "./attendanceLogic";

/** Correcting one saved mark: what was seen, times, and a remark. */
function RecordForm({ record, onClose, onDone }: { record: AttendanceRecord; onClose: () => void; onDone: (row: AttendanceRecord) => void }) {
  const [remarks, setRemarks] = useState(record.remarks ?? "");
  const save = useMutation({ mutationFn: attendanceService.updateRecord, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      id: record.id,
      data: { status: text("status") as AttendanceRecord["status"], ...(record.timetable_slot ? {} : { period: text("period") as AttendanceRecord["period"] }), time_in: text("time_in") ?? undefined, time_out: text("time_out") ?? undefined, remarks: remarks.trim() },
    });
  };
  return (
    <FormModal id="attendance-record" title={`Correct: ${record.student_name}, ${day(record.date)}`} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "The record could not be saved.") : undefined} saveLabel="Save" note="The record then shows you as the one who marked it." onClose={onClose} onSubmit={submit}>
      <FormSection title={registerLabel(record)} divided={false}>
        <Field label="Mark">
          <select name="status" defaultValue={record.status} className="input mt-1">
            {STATUSES.map((row) => <option key={row.value} value={row.value}>{row.label}</option>)}
          </select>
        </Field>
        {!record.timetable_slot && (
          <Field label="Part of the day">
            <select name="period" defaultValue={record.period} className="input mt-1">
              {PERIODS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </Field>
        )}
        <Field label="Arrived" optional>
          <input type="time" name="time_in" defaultValue={clock(record.time_in)} className="input mt-1" />
        </Field>
        <Field label="Left" optional>
          <input type="time" name="time_out" defaultValue={clock(record.time_out)} className="input mt-1" />
        </Field>
        <Field label="Remark" optional as="div" className="sm:col-span-2">
          <AIAssistField rows={2} value={remarks} onChange={(next) => setRemarks(next)} placeholder="e.g. Went home sick at 11, collected by her aunt." context={{ key: "attendance.note", params: { kind: "remark", id: record.id } }} draftLabel="Word it" draftHint="Jot what happened first; it never guesses a reason." />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Every saved mark in a period, with who took it; open one to read or correct it. */
export function RecordsPanel({ classes, canCorrect }: { classes: { id: number; name: string }[]; canCorrect: boolean }) {
  const [from, setFrom] = useState(daysFromTodayIso(-6));
  const [to, setTo] = useState(todayIso());
  const [classId, setClassId] = useState(0);
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<AttendanceRecord | null>(null);
  const records = useQuery({
    queryKey: ["attendance", "records", from, to, classId],
    queryFn: () => attendanceService.getAttendanceRecords({ date_from: from, date_to: to, ...(classId ? { current_class: classId } : {}) }),
  });

  const columns: RegisterColumn<AttendanceRecord>[] = [
    { header: "Date", cell: (row) => day(row.date), exportValue: (row) => day(row.date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_id}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[11rem]",
    },
    { header: "Learner number", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name, className: "text-gray-700" },
    { header: "Register", cell: (row) => registerLabel(row), exportValue: (row) => registerLabel(row), className: "text-gray-700" },
    { header: "Mark", cell: (row) => <StatusDot {...statusDot(row.status)} />, exportValue: (row) => statusOf(row.status).label },
    { header: "Times", cell: (row) => (row.time_in ? `${clock(row.time_in)}${row.time_out ? `–${clock(row.time_out)}` : ""}` : "—"), exportValue: (row) => [clock(row.time_in), clock(row.time_out)].filter(Boolean).join("–"), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Remark", cell: (row) => row.remarks || "—", exportValue: (row) => row.remarks ?? "", className: "max-w-[16rem] truncate text-gray-700" },
    { header: "Marked by", cell: (row) => row.marked_by_name || "—", exportValue: (row) => row.marked_by_name, className: "text-gray-700" },
  ];
  const record = viewing !== null ? records.data?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <div className="card">
        <div className="card-content grid gap-3 sm:grid-cols-4">
          <Field label="From">
            <input type="date" max={to} value={from} onChange={(e) => setFrom(e.target.value)} className="input mt-1" />
          </Field>
          <Field label="To">
            <input type="date" min={from} max={todayIso()} value={to} onChange={(e) => setTo(e.target.value)} className="input mt-1" />
          </Field>
          <Field label="Class">
            <select value={classId} onChange={(e) => setClassId(Number(e.target.value))} className="input mt-1">
              <option value={0}>Every class I can see</option>
              {classes.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
          </Field>
          <div className="flex items-end gap-1.5 pb-0.5">
            {[["Today", 0], ["This week", -6], ["30 days", -29]].map(([label, offset]) => (
              <button key={label} type="button" onClick={() => { setFrom(daysFromTodayIso(Number(offset))); setTo(todayIso()); }} className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ClientRegister
        key={`${from}-${to}-${classId}`}
        title="Records"
        description="Every mark saved in the period: whole-day and lesson registers. Open one to see it in full or correct it."
        rows={records.data}
        config={recordConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["record", "records"]}
        exportName={{ file: `attendance-${from}-to-${to}`, title: `Attendance ${from} to ${to}` }}
        isLoading={records.isLoading}
        isError={records.isError}
        onRetry={() => records.refetch()}
        emptyText="No registers were saved in this period."
      />
      {record && (
        <DetailsModal
          title={record.student_name}
          headline={{ label: [record.class_name, record.student_id].filter(Boolean).join(" · "), value: statusOf(record.status).label, note: `${new Date(`${record.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · ${registerLabel(record)}` }}
          status={statusDot(record.status)}
          facts={[
            ["Arrived", clock(record.time_in) || undefined],
            ["Left", clock(record.time_out) || undefined],
            ["Marked by", record.marked_by_name || undefined],
            ["Last changed", new Date(record.updated_at).toLocaleString()],
            ["Parent told", record.parent_notified ? `Yes${record.notification_sent_at ? `, ${day(record.notification_sent_at)}` : ""}` : "No"],
            ["Checked", record.is_verified ? "Yes" : "No"],
          ]}
          sections={[{ title: "Remark", body: record.remarks }, { title: "Check notes", body: record.verification_notes }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canCorrect && (
                <button type="button" onClick={() => setEditing(record)} className="btn btn-primary gap-1.5"><Pencil className="h-4 w-4" /> Correct it</button>
              )}
            </>
          }
        />
      )}
      {editing && (
        <RecordForm
          record={editing}
          onClose={() => setEditing(null)}
          onDone={(row) => {
            setEditing(null);
            setNotice({ tone: "success", text: `${row.student_name}'s mark for ${day(row.date)} is now ${statusOf(row.status).label.toLowerCase()}.` });
            records.refetch();
          }}
        />
      )}
    </div>
  );
}
